/**
 * Map failed source loads (bootstrap + retry) to user-facing Spanish copy.
 * Live clients throw plain ApiError objects (`{ code, message, status }`), not Error.
 */

import {
  isProviderAuthFailureText,
  sourceCredentialsRejectedMessage,
  sourceNotConfiguredMessage,
  type ConnectableSource,
} from './sourceConnection';

export interface ApiErrorParts {
  code: string;
  message: string;
  status: number | null;
}

export function readApiErrorParts(err: unknown): ApiErrorParts {
  const record = err && typeof err === 'object' ? (err as Record<string, unknown>) : null;
  const rawMessage = record?.message;
  const message =
    typeof rawMessage === 'string' && rawMessage.trim()
      ? rawMessage
      : err instanceof Error && err.message
        ? err.message
        : '';
  const status = record && 'status' in record ? Number(record.status) : NaN;
  return {
    code: record && 'code' in record ? String(record.code ?? '') : '',
    message,
    status: Number.isFinite(status) ? status : null,
  };
}

function withCodeSuffix(parts: ApiErrorParts, err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (!err || typeof err !== 'object') return fallback;
  const detail = parts.message || fallback;
  const code = parts.code || 'UNKNOWN_ERROR';
  const status = parts.status != null ? ` (HTTP ${parts.status})` : '';
  const retryAfter = (err as { retryAfter?: unknown }).retryAfter;
  const retry = typeof retryAfter === 'number' ? ` Reintenta en ${retryAfter}s.` : '';
  return `${detail} [${code}]${status}${retry}`;
}

function jiraMessage(parts: ApiErrorParts): string {
  const { code, message, status } = parts;
  if (code === 'JIRA_NOT_CONFIGURED') return sourceNotConfiguredMessage('jira');
  if (code === 'JIRA_AUTH_REJECTED' || status === 401 || isProviderAuthFailureText(message)) {
    return sourceCredentialsRejectedMessage('jira');
  }
  if (status === 410 || /\b410\b/.test(message) || /search\/jql/i.test(message)) {
    return 'Atlassian rechazó la búsqueda de issues (API antigua). Zunelo debe usar /rest/api/3/search/jql; actualiza el despliegue si sigue fallando.';
  }
  if (message.includes('JIRA_NOT_CONFIGURED') || /not configured/i.test(message)) {
    return sourceNotConfiguredMessage('jira');
  }
  return message || 'Error al cargar tickets de Jira.';
}

function githubMessage(parts: ApiErrorParts): string {
  const { code, message, status } = parts;
  if (code === 'GITHUB_TOKEN_MISSING') return sourceNotConfiguredMessage('github');
  if (
    code === 'GITHUB_AUTH_REJECTED' ||
    status === 401 ||
    isProviderAuthFailureText(message) ||
    /invalid.*token/i.test(message)
  ) {
    return sourceCredentialsRejectedMessage('github');
  }
  if (code === 'GITHUB_HYDRATE_EMPTY') {
    return 'GitHub encontró PRs pero no pudo cargarlos (permisos del PAT o acceso al repo). Abre Settings → GitHub y revisa los scopes.';
  }
  if (status === 403 || /\b403\b/.test(message) || /forbidden/i.test(message)) {
    return 'El PAT de GitHub no tiene permisos suficientes. Revisa scopes en Settings → GitHub.';
  }
  if (message.includes('GITHUB_TOKEN_MISSING') || /not configured/i.test(message)) {
    return sourceNotConfiguredMessage('github');
  }
  return message || 'Error al cargar pull requests de GitHub.';
}

function gmailMessage(parts: ApiErrorParts): string {
  const { code, message, status } = parts;
  if (code === 'GMAIL_NOT_CONFIGURED') return sourceNotConfiguredMessage('gmail');
  if (code === 'GMAIL_AUTH_REJECTED' || status === 401 || isProviderAuthFailureText(message)) {
    return sourceCredentialsRejectedMessage('gmail');
  }
  if (
    message.includes('GMAIL_NOT_CONFIGURED') ||
    /not configured|no está conectado/i.test(message)
  ) {
    return sourceNotConfiguredMessage('gmail');
  }
  return message || 'Error al cargar correos de Gmail.';
}

export function slackLoadErrorMessage(err: unknown): string {
  const parts = readApiErrorParts(err);
  if (parts.code === 'SLACK_TOKEN_MISSING' || parts.code === 'SLACK_NOT_CONFIGURED') {
    return sourceNotConfiguredMessage('slack');
  }
  const raw = withCodeSuffix(parts, err, 'Error al sincronizar hilos de Slack.');
  const normalized = raw.toLowerCase();
  if (parts.code === 'SLACK_AUTH_REJECTED' || isProviderAuthFailureText(raw)) {
    return sourceCredentialsRejectedMessage('slack');
  }
  if (normalized.includes('missing_scope')) {
    return `${raw} La app de Slack no tiene los permisos requeridos; agrega el scope indicado y reinstálala.`;
  }
  if (normalized.includes('ratelimited') || normalized.includes('429')) {
    return `${raw} Slack limitó temporalmente las solicitudes.`;
  }
  return raw;
}

/** One entry point for bootstrap and retry failures of any attention source. */
export function sourceLoadErrorMessage(source: ConnectableSource | string, err: unknown): string {
  if (source === 'jira') return jiraMessage(readApiErrorParts(err));
  if (source === 'github') return githubMessage(readApiErrorParts(err));
  if (source === 'slack') return slackLoadErrorMessage(err);
  if (source === 'gmail') return gmailMessage(readApiErrorParts(err));
  return withCodeSuffix(readApiErrorParts(err), err, `Fallo al reintentar ${source}`);
}
